import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import './Dashboard.css';

const Dashboard = () => {
  const { user, logout, fetchUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [recentChecks, setRecentChecks] = useState([]);

  useEffect(() => {
    fetchUser();
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const historyResponse = await axios.get(`${API}/grammar/history`);
      setRecentChecks(historyResponse.data.slice(0, 5));
    } catch (error) {
      console.error('Dashboard load error:', error);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const usagePercentage = user?.is_premium
    ? 100
    : ((user?.daily_limit - (user?.remaining || 0)) / user?.daily_limit) * 100;

  return (
    <div className="dashboard-container">
      <div className="glass-background"></div>

      {/* ⭐ NAVBAR */}
      <nav className="dashboard-nav fade-in">
        <div className="nav-brand" onClick={() => navigate('/')}>
          <img src="/icons/logo-512.png" alt="GrammAA Logo" className="nav-logo" />
          <span className="brand-text">GrammAA</span>
        </div>

        <div className="nav-links">
          <Button variant="ghost" onClick={() => navigate('/checker')}>Checker</Button>
          <Button variant="ghost" onClick={() => navigate('/history')}>History</Button>
          <Button variant="ghost" onClick={() => navigate('/pricing')}>Pricing</Button>

          {user?.is_admin && (
            <Button variant="ghost" onClick={() => navigate('/admin')}>Admin</Button>
          )}

          <Button variant="outline" onClick={handleLogout}>Logout</Button>
        </div>
      </nav>

      {/* ⭐ CONTENT SECTION */}
      <div className="dashboard-content fade-in">

        {/* HEADER */}
        <div className="dashboard-header">
          <div>
            <h1>Dashboard</h1>
            <p className="dashboard-subtitle">{user?.email}</p>
          </div>

          {user?.is_premium && (
            <Badge className="premium-badge">Premium Member</Badge>
          )}
        </div>

        {/* STATS GRID */}
        <div className="dashboard-grid">

          {/* ✔ Today's Usage */}
          <Card className="stat-card glass-panel">
            <h3>Today's Usage</h3>

            <div className="usage-display">
              <div className="usage-numbers">
                <span className="usage-count">
                  {user?.is_premium
                    ? 'Unlimited'
                    : `${user?.used_today || 0}/${user?.daily_limit || 3}`}
                </span>
                <span className="usage-label">checks used</span>
              </div>

              {!user?.is_premium && (
                <Progress value={usagePercentage} className="usage-progress" />
              )}
            </div>

            {!user?.is_premium && user?.remaining === 0 && (
              <Button onClick={() => navigate('/pricing')} className="glass-btn upgrade-btn">
                Upgrade to Premium
              </Button>
            )}
          </Card>

          {/* ✔ Total Checks */}
          <Card className="stat-card glass-panel">
            <h3>Total Checks</h3>
            <div className="stat-value">{recentChecks.length}</div>
            <p className="stat-label">All time grammar checks</p>
          </Card>

          {/* ✔ Account Status */}
          <Card className="stat-card glass-panel">
            <h3>Account Status</h3>
            <div className="stat-value">
              {user?.is_premium ? 'Premium' : 'Free'}
            </div>
            <p className="stat-label">
              {user?.is_premium ? 'Unlimited checks' : `${user?.daily_limit} checks/day`}
            </p>
          </Card>
        </div>

        {/* RECENT CHECKS PANEL */}
        <Card className="recent-checks glass-panel">
          <div className="recent-header">
            <h2>Recent Checks</h2>
            <Button variant="ghost" onClick={() => navigate('/history')}>
              View All
            </Button>
          </div>

          {recentChecks.length > 0 ? (
            <div className="checks-list">
              {recentChecks.map((check, index) => (
                <div
                  key={check.id}
                  className="check-item"
                  onClick={() => navigate('/history')}
                >
                  <div className="check-content">
                    <p className="check-text">
                      {check.original_text.substring(0, 100)}
                      {check.original_text.length > 100 ? '...' : ''}
                    </p>
                    <span className="check-date">
                      {new Date(check.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  <Badge className="score-badge">
                    {check.grammar_score}/100
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No grammar checks yet</p>
              <Button onClick={() => navigate('/checker')} className="glass-btn">
                Start Checking
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
